import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall } from '../../api';
import i18n from '../../i18n/config';
import { setI18nLanguage } from '../../i18n/config';

const PendingDeleteRequests = ({ userRole, orgId, getToken, onBack, onHome, onEditProfile }) => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const { t: tc } = useTranslation('common');

  const [requests, setRequests] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionError, setActionError] = useState('');
  const [result, setResult] = useState(null);
  const [reviewNote, setReviewNote] = useState('');

  const ALLOWED_ROLES = ['treasury', 'admin'];
  const hasAccess = ALLOWED_ROLES.includes(userRole);

  const fetchRequests = useCallback(async () => {
    if (!orgId) return;
    setLoadingData(true);
    try {
      const data = await apiCall(`/api/expenses/deletion-requests/?org_id=${orgId}`, {}, getToken);
      
      const mapped = data.results ? data.results : data;
      setRequests(mapped.map(r => ({
        id: r.id,
        transactionName: r.transaction_name || (r.transaction_type === 'expense' ? `Expense #${r.transaction_id}` : `Contribution #${r.transaction_id}`),
        type: r.transaction_type === 'expense' ? 'Expense' : 'Contribution',
        amount: r.amount || 'N/A',
        requestedBy: r.requested_by_name || 'Unknown',
        requestedDate: r.requested_at ? new Date(r.requested_at).toLocaleDateString() : '',
        reason: r.reason || '',
      })));
    } catch (err) {
      setFetchError(err.type === 'NETWORK_ERROR' ? tc('networkError') : (err.detail || err.message || tc('error')));
    } finally {
      setLoadingData(false);
    }
  }, [orgId, getToken, tc]);

  useEffect(() => {
    if (orgId && hasAccess) {
      fetchRequests();
    }
  }, [orgId, hasAccess, fetchRequests]);

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);
  
    // Close the language menu on outside click.
    useEffect(() => {
      if (!langMenuOpen) return;
      const handleClickOutside = (e) => {
        if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
          setLangMenuOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [langMenuOpen]);
  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#dc2626" />
            </svg>
          </div>
          <p className="text-base font-medium text-gray-900 mb-1">Access Denied</p>
          <p className="text-sm text-gray-500 mb-6">Only Treasury and Admin can approve/reject deletion requests.</p>
          <button
            onClick={onBack}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handleApprove = (request) => {
    setConfirmAction(request);
    setActionType('approve');
  };

  const handleReject = (request) => {
    setConfirmAction(request);
    setActionType('reject');
  };

  const handleConfirmAction = async () => {
    setLoading(true);
    try {
      const endpoint = actionType === 'approve' ? 'approve' : 'reject';
      
      const body = actionType === 'reject' ? { review_note: reviewNote } : {};
      
      await apiCall(
        `/api/expenses/deletion-requests/${confirmAction.id}/${endpoint}/`,
        { method: 'POST', body },
        getToken
      );

      // Remove from pending list
      setRequests(requests.filter(r => r.id !== confirmAction.id));

      // Show result
      setResult({
        type: actionType,
        transaction: confirmAction.transactionName,
        requestedBy: confirmAction.requestedBy,
        date: new Date().toLocaleDateString(),
      });

      // Clear modals
      setConfirmAction(null);
      setActionType(null);

      // Clear result after 3 seconds
      setTimeout(() => setResult(null), 3000);
    } catch (err) {
      setActionError(err.type === 'NETWORK_ERROR' ? tc('networkError') : (err.detail || err.message || tc('error')));
    } finally {
      setLoading(false);
    }
  };

  // Result screen — full-page replacement per §6
  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center max-w-sm w-full">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            result.type === 'approve' ? 'bg-green-100' : 'bg-red-100'
          }`}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              {result.type === 'approve' ? (
                <path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              ) : (
                <path d="M6 18L18 6M6 6l12 12" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
              )}
            </svg>
          </div>
          <p className="text-base font-medium text-gray-900 mb-1">
            Deletion {result.type === 'approve' ? 'Approved' : 'Rejected'}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {result.transaction} deletion request {result.type === 'approve' ? 'approved' : 'rejected'}
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 mb-6 text-left">
            <p className="text-xs text-gray-500 mb-1">Requested by: <strong className="text-gray-700">{result.requestedBy}</strong></p>
            <p className="text-xs text-gray-500">Date: <strong className="text-gray-700">{result.date}</strong></p>
          </div>
          <button
            onClick={onHome}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3 relative">
        <button onClick={onHome} aria-label="Home" title="Home" className="text-blue-600 hover:text-blue-700 flex-shrink-0">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-900 flex-1">Pending Delete Requests</span>

        <div className="flex-1" />

        
        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>
        
        {/* Profile avatar + dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                Edit My Profile
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          Pending Delete Requests
        </h1>

        {loadingData ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-500">Loading requests...</p>
          </div>
        ) : fetchError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-sm text-red-600">{fetchError}</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-500">No pending deletion requests</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {requests.map(request => (
              <div key={request.id} className="bg-white border border-gray-200 rounded-lg p-3.5">
                <div className="mb-2.5">
                  <p className="text-sm font-semibold text-gray-900">{request.transactionName}</p>
                  <p className="text-xs text-gray-500 mt-1">{request.type} - ₹{request.amount}</p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2.5 mb-3">
                  <p className="text-xs text-gray-500 mb-1">
                    <strong className="text-gray-700">Requested by:</strong> {request.requestedBy}
                  </p>
                  <p className="text-xs text-gray-500 mb-1">
                    <strong className="text-gray-700">Date:</strong> {request.requestedDate}
                  </p>
                  <p className="text-xs text-gray-500">
                    <strong className="text-gray-700">Reason:</strong> {request.reason || 'Not specified'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleApprove(request)}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
                  >
                    Confirm Approve
                  </button>
                  <button
                    onClick={() => handleReject(request)}
                    className="bg-red-500 hover:bg-red-600 text-white rounded-lg py-2.5 text-sm font-medium transition"
                  >
                    Confirm Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 mx-4 w-full max-w-xs shadow-xl">
            <p className="text-sm font-medium text-gray-900 mb-3">
              {actionType === 'approve' ? 'Approve Deletion?' : 'Reject Deletion?'}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              You are about to {actionType === 'approve' ? 'approve' : 'reject'} the deletion request for:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 mb-4">
              <p className="text-xs font-semibold text-gray-900">{confirmAction.transactionName}</p>
              <p className="text-xs text-gray-500 mt-1">{confirmAction.type}</p>
            </div>
            
            {actionType === 'reject' && (
              <input
                type="text"
                placeholder="Optional rejection reason"
                value={reviewNote}
                onChange={e => setReviewNote(e.target.value)}
                className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0 mb-4"
              />
            )}

            {actionError && (
              <div className="mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                <p className="text-xs text-red-600">{actionError}</p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => setConfirmAction(null)}
                disabled={loading}
                className="flex-1 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 text-sm font-medium transition disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={loading}
                className={`flex-1 text-white rounded-lg py-2.5 text-sm font-medium transition disabled:cursor-not-allowed ${
                  actionType === 'approve'
                    ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'
                    : 'bg-red-500 hover:bg-red-600 disabled:bg-red-300'
                }`}
              >
                {loading ? 'Processing…' : actionType === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingDeleteRequests;